import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, Grid, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import { useCreateSimpleStepItem } from "../../hooks/simple-steps/useCreateSimpleStepItem";
import { useUpdateSimpleStepItem } from "../../hooks/simple-steps/useUpdateSimpleStepItem";
import FormInput from "../inputs/FormInput";
import LanguageToggle from "../global/LanguageToggle";

const FormAddEditSimpleStepItem = ({ currentData, isEdit = false, onSubmitSuccess }) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng.startsWith("ar");
    const initialLang = localStorage.getItem("i18nextLng") || i18next.language || "ar";
    const [activeLang, setActiveLang] = useState(initialLang.startsWith("ar") ? "ar" : "en");

    const [titleAr, setTitleAr] = useState("");
    const [titleEn, setTitleEn] = useState("");
    const [descriptionAr, setDescriptionAr] = useState("");
    const [descriptionEn, setDescriptionEn] = useState("");
    const [image, setImage] = useState(null);
    const [isActive, setIsActive] = useState(true);
    const [sortOrder, setSortOrder] = useState(0);

    const { createItemMutate, isPending: isCreating } = useCreateSimpleStepItem();
    const { updateItemMutate, isPending: isUpdating } = useUpdateSimpleStepItem();

    const loadingSubmit = isCreating || isUpdating;

    useEffect(() => {
        if (isEdit && currentData) {
            setTitleAr(currentData?.title?.ar || "");
            setTitleEn(currentData?.title?.en || "");
            setDescriptionAr(currentData?.description?.ar || "");
            setDescriptionEn(currentData?.description?.en || "");
            setImage(currentData?.image || null);
            setIsActive(currentData?.is_active ?? true);
            setSortOrder(currentData?.sort_order ?? 0);
        }
    }, [isEdit, currentData]);

    const getImageName = (imageVal) => {
        if (!imageVal) return "";
        if (typeof imageVal === 'string') {
            return imageVal.substr(imageVal.lastIndexOf('/') + 1);
        }
        if (imageVal?.file_name) return imageVal.file_name;
        if (imageVal?.name) return imageVal.name;
        return imageVal;
    };

    const handleImageChange = (e) => {
        setImage(e.target.value);
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const payload = {
            title: {
                ar: titleAr || titleEn,
                en: titleEn || titleAr,
            },
            description: {
                ar: descriptionAr || descriptionEn,
                en: descriptionEn || descriptionAr,
            },
            image: getImageName(image),
            is_active: isActive ? 1 : 0,
            sort_order: Number(sortOrder),
        };

        if (isEdit) {
            updateItemMutate({ id: currentData.id, data: payload });
        } else {
            createItemMutate(payload);
        }

        // if (onSubmitSuccess) onSubmitSuccess();
    };

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
            <Paper sx={{ p: 3, borderRadius: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 2 }}>
                    <Typography variant="h6">
                        {isEdit ? (isArabic ? "تعديل الخطوة" : "Edit Step") : (isArabic ? "إضافة خطوة جديدة" : "Add New Step")}
                    </Typography>
                    <LanguageToggle activeLang={activeLang} onChange={(lng) => setActiveLang(lng)} showLabel={true} />
                </Box>

                <Grid container spacing={3}>
                    <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <FormInput
                            type="file"
                            name="image"
                            labelImage={isArabic ? "صورة الخطوة" : "Step Image"}
                            value={image}
                            onChange={handleImageChange}
                            width={200}
                            height={120}
                        />
                    </Grid>

                    {activeLang === "ar" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="العنوان (عربي)"
                                placeholder="أدخل العنوان بالعربي"
                                value={titleAr}
                                onChange={(e) => setTitleAr(e.target.value)}
                                required
                                fullWidth
                            />
                        </Grid>
                    )}

                    {activeLang === "en" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="Title (English)"
                                placeholder="Enter title in English"
                                value={titleEn}
                                onChange={(e) => setTitleEn(e.target.value)}
                                required
                                fullWidth
                            />
                        </Grid>
                    )}
                    <Grid item xs={12} md={6}>
                        <FormInput
                            type="number"
                            label={isArabic ? "ترتيب العرض" : "Sort Order"}
                            placeholder="0"
                            value={sortOrder}
                            onChange={(e) => setSortOrder(e.target.value)}
                            fullWidth
                        />
                    </Grid>
                    {activeLang === "ar" && (
                        <Grid item xs={12}>
                            <FormInput
                                type="textarea"
                                label="الوصف (عربي)"
                                placeholder="أدخل الوصف بالعربي"
                                value={descriptionAr}
                                onChange={(e) => setDescriptionAr(e.target.value)}
                                multiline
                                rows={4}
                                fullWidth
                            />
                        </Grid>
                    )}

                    {activeLang === "en" && (
                        <Grid item xs={12}>
                            <FormInput
                                type="textarea"
                                label="Description (English)"
                                placeholder="Enter description in English"
                                value={descriptionEn}
                                onChange={(e) => setDescriptionEn(e.target.value)}
                                multiline
                                rows={4}
                                fullWidth
                            />
                        </Grid>
                    )}
                    <Grid item xs={12} md={6}>
                        <FormInput
                            type="switch"
                            label={isArabic ? "نشط" : "Active"}
                            name="is_active"
                            checked={isActive}
                            onChange={(e) => setIsActive(e.target.checked)}
                        />
                    </Grid>

                </Grid>

                <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={loadingSubmit}
                        sx={{ minWidth: 120 }}
                    >
                        {loadingSubmit ? (isArabic ? "جاري الحفظ..." : "Saving...") : (isArabic ? "حفظ" : "Save")}
                    </Button>
                </Box>
            </Paper>
        </Box>
    );
};

export default FormAddEditSimpleStepItem;
